import subpixel from '../util/canvasSubpixelOffset';
import LineTool from './LineTool';

/** Returns Calculator Tool */
export default class ReturnsCalculatorTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'returnscalculator',
			name: 'Returns Calculator',
			value: null,
			style: this.core.getStyle('tool.returnsCalculator')
		};
	}

	renderOverlay(ctx, dataPointLeft, dataPointRight, leftCoords, rightCoords) {
		ctx.beginPath();
		ctx.textBaseline = 'top';
		ctx.fillStyle = '#FFF';

		const width = 163;
		const height = 102;
		const mid = leftCoords[0] + ((rightCoords[0] - leftCoords[0]) / 2);
		const left = mid - (width / 2);
		const top = (this.panel.size.height / 2) - (height / 2);
		let leftClose = dataPointLeft.close;
		let rightClose = dataPointRight.close;
		const leftHeader = this.panel.core.timeFormat('%x')(new Date(dataPointLeft.date));
		const rightHeader = this.panel.core.timeFormat('%x')(new Date(dataPointRight.date));
		const headerValue = (leftHeader === rightHeader) ? leftHeader : `${leftHeader} - ${rightHeader}`;

		// AC-275 convert normalized values to actuals if a comparison exists
		if (
			this.panel.core.params.symbolCompare.length ||
			this.panel.core.comparisons.length
		) {
			leftClose = this.panel.normalizedToActual(leftClose);
			rightClose = this.panel.normalizedToActual(rightClose);
		}

		// white background with dropshadow
		ctx.shadowColor = '#666666';
		ctx.shadowBlur = 15;
		ctx.rect(left, top, width, height);
		ctx.fill();
		ctx.shadowColor = null;
		ctx.shadowBlur = null;

		// text
		const padding = 12;
		const rowHeight = 15;
		const rowHeaderPad = 5;
		let rowTop = top + padding;
		let rowLeft = left + padding;

		// header
		ctx.textAlign = 'center';
		ctx.font = 'bold 12px Arial';
		ctx.fillStyle = '#000000';
		ctx.fillText(headerValue, left + width / 2, rowTop);

		// first column
		ctx.textAlign = 'left';
		rowTop += rowHeight + rowHeaderPad;
		ctx.font = '11px Arial';
		ctx.fillStyle = '#888888';
		ctx.fillText('Start', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('End', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Change', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Change Percent', rowLeft, rowTop);

		// second column
		const { yAxis } = this.panel;
		ctx.textAlign = 'right';
		rowTop = top + padding + rowHeight + rowHeaderPad;
		rowLeft = left + width - padding;
		ctx.fillStyle = '#444444';
		ctx.fillText(
			yAxis.getFormatValue(
				leftClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				rightClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillStyle = (rightClose >= leftClose) ? 'green' : 'red';
		ctx.fillText(
			yAxis.getFormatValue(
				rightClose - leftClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		const percentChange = (Number(rightClose) - Number(leftClose)) / Math.abs(Number(leftClose));
		ctx.fillText(
			yAxis.getFormatValue(
				percentChange,
				'0,.2%' // Should this format specifier be exposed via styles?
			),
			rowLeft,
			rowTop
		);
	}

	render(panel = this.panel) {
		const { isUpper } = panel;
		const ctx = panel.rootToolContext;
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		let [leftCoords, rightCoords] = coords;
		const height = panel.size.height;

		if (rightCoords[0] < leftCoords[0]) {
			// 2nd handle is further left, so flip
			[rightCoords, leftCoords] = coords;
		}

		// get entry and exit y positions
		const dataPointLeft = this.getDataPointFromX(leftCoords[0]);
		const dataPointRight = this.getDataPointFromX(rightCoords[0]);
		const dataYLeft = subpixel.px(
			Number(panel.yAxis.primaryScale(dataPointLeft.close))
		);
		const dataYRight = subpixel.px(
			Number(panel.yAxis.primaryScale(dataPointRight.close))
		);

		// inner fill
		ctx.save();
		ctx.fillStyle = this.params.style.center.background.color;
		ctx.fillRect(leftCoords[0], 0, rightCoords[0] - leftCoords[0], height);
		ctx.restore();

		// left and right fills
		ctx.fillStyle = this.params.style.side.background.color;
		ctx.fillRect(1, 1, leftCoords[0], height - 1);
		ctx.fillRect(
			rightCoords[0],
			1,
			panel.size.width - rightCoords[0],
			height - 1
		);
		ctx.restore();

		// vertical left edge line
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.side.color;
		ctx.lineWidth = this.params.style.side.width;
		ctx.moveTo(leftCoords[0], 0);
		ctx.lineTo(leftCoords[0], height);

		// vertical right edge line
		ctx.moveTo(rightCoords[0], 0);
		ctx.lineTo(rightCoords[0], height);
		ctx.stroke();

		this.handle[0].render();
		this.handle[1].render();

		if (isUpper) {
			// horizontal entry line
			ctx.beginPath();
			ctx.setLineDash([2, 2]);
			ctx.moveTo(0, dataYLeft);
			ctx.lineTo(leftCoords[0], dataYLeft);

			// horizontal exit line
			ctx.moveTo(rightCoords[0], dataYRight);
			ctx.lineTo(panel.size.width - 1, dataYRight);

			ctx.stroke();
			ctx.setLineDash([]);
		}

		if (isUpper) {
			this.renderOverlay(
				ctx,
				dataPointLeft,
				dataPointRight,
				leftCoords,
				rightCoords
			);
		}

		panel.core.onToolRender(this);
	}

	renderLower(panel) {
		return this.render(panel);
	}
}
